import { BadgePercent, CalendarDays, Headset, Menu, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ibe/ui-web';
import { useApi } from '../api/api-context.js';
import { CustomSelect } from '../components/custom-select.js';
import { LoadingScreen } from '../components/loading-screen.js';
import { Sidebar } from '../components/sidebar.js';
import { useCurrency, CURRENCY_OPTIONS } from '../currency-context.js';
import { useLanguage, LANGUAGE_OPTIONS } from '../language-context.js';
import { useSite, useSiteReady, PageTitleProvider } from '../site-context.js';
import { useTranslation } from '../use-translation.js';
import { SignInDialog } from '../../features/auth/components/sign-in-dialog.js';
import { SignUpDialog } from '../../features/auth/components/sign-up-dialog.js';
import { useBooking } from '../../features/booking/state/booking-context.js';

const PREFERENCE_TRANSITION_MS = 500;

const TRUST_ITEMS = [
  {
    icon: BadgePercent,
    title: 'site.trust.bestPrice',
    description: 'site.trust.bestPriceDesc',
  },
  {
    icon: CalendarDays,
    title: 'site.trust.flexibleBooking',
    description: 'site.trust.flexibleBookingDesc',
  },
  {
    icon: ShieldCheck,
    title: 'site.trust.securePayments',
    description: 'site.trust.securePaymentsDesc',
  },
  {
    icon: Headset,
    title: 'site.trust.localSupport',
    description: 'site.trust.localSupportDesc',
  },
] as const;

function BrandMark({
  name,
  logoUrl,
}: {
  readonly name: string;
  readonly logoUrl: string;
}): React.JSX.Element | null {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  if (logoUrl === '' || failed) {
    if (name === '') return null;
    return (
      <span
        aria-hidden="true"
        className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-container text-sm font-bold text-on-primary-container"
      >
        {name.charAt(0).toUpperCase()}
      </span>
    );
  }

  return (
    <span className="relative inline-flex h-8 w-8 items-center justify-center">
      {!loaded && (
        <span aria-hidden="true" className="absolute inset-0 animate-pulse rounded-md bg-muted" />
      )}
      <img
        alt={name}
        src={logoUrl}
        onLoad={() => {
          setLoaded(true);
        }}
        onError={() => {
          setFailed(true);
        }}
        className={`h-8 w-8 object-contain transition-opacity ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </span>
  );
}

export function SiteLayout(): React.JSX.Element {
  const site = useSite();
  const siteReady = useSiteReady();
  const api = useApi();
  const booking = useBooking();
  const navigate = useNavigate();
  const location = useLocation();
  const { currency, setCurrency } = useCurrency();
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  // Every route that is about one property. The header swaps the tenant
  // tagline for the property the guest is looking at — the same pattern the
  // legacy portal's header follows once a property is chosen.
  const rateRoute = useMatch('/properties/:propertyId');
  const roomRoute = useMatch('/properties/:propertyId/rooms/:roomTypeId');
  const legacyRateRoute = useMatch('/room-rate/:propertyId');
  const legacyRoomRoute = useMatch('/room-rate/:propertyId/:roomTypeId');
  const legacyInfoRoute = useMatch('/room-info/:propertyId/:roomTypeId');
  const searchRoute = useMatch('/search');
  const searchedPropertyId =
    searchRoute === null ? null : new URLSearchParams(location.search).get('propertyId');
  const routePropertyId =
    (rateRoute ?? roomRoute ?? legacyRateRoute ?? legacyRoomRoute ?? legacyInfoRoute)?.params
      .propertyId ?? searchedPropertyId;
  // Booking state remembers the last property the guest actually viewed —
  // matching it against the route keeps a stale name off unrelated pages.
  const viewingProperty =
    routePropertyId !== null && booking.state.propertyId === routePropertyId ? booking.state : null;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);
  // Header auth opens the in-context dialogs — routing to /auth/sign-in would
  // abandon whatever the guest was doing (checkout, rate browsing).
  const [authDialog, setAuthDialog] = useState<'sign-in' | 'join' | null>(null);
  // Full-screen veil while a currency/language change re-renders the page and
  // downstream queries refetch — the alternative is watching every price and
  // label flip mid-paint.
  // Full-screen veil while a currency change re-renders the page and downstream
  // queries refetch — language changes are already covered by the
  // ContentTranslationProvider gate, which renders LoadingScreen itself.
  const [preferenceTransitioning, setPreferenceTransitioning] = useState(false);
  const transitionTimeout = useRef<number | null>(null);
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    return () => {
      if (transitionTimeout.current !== null) {
        window.clearTimeout(transitionTimeout.current);
      }
    };
  }, []);

  const withPreferenceTransition = useCallback((apply: () => void) => {
    if (transitionTimeout.current !== null) {
      window.clearTimeout(transitionTimeout.current);
    }
    setPreferenceTransitioning(true);
    apply();
    transitionTimeout.current = window.setTimeout(() => {
      transitionTimeout.current = null;
      setPreferenceTransitioning(false);
    }, PREFERENCE_TRANSITION_MS);
  }, []);

  const isSignedIn = booking.state.accessToken !== null && booking.state.accessToken !== '';
  const activeCurrency = booking.state.selectedRooms.length > 0 ? booking.state.currency : currency;

  // Keep the shared API client's bearer token in step with the booking
  // state. sessionStorage restores `state.accessToken` across a reload but
  // the client is a fresh singleton — without this it would send no
  // Authorization header and the server would correctly treat the guest as
  // anonymous, silently dropping member-priced offers.
  useEffect(() => {
    api.setAccessToken(booking.state.accessToken);
  }, [api, booking.state.accessToken]);

  // When the client's session actually ends (401 + refresh refused), mirror
  // it into booking state so the header stops showing a stale sign-in — the
  // guest stays where they are and can sign back in via the dialog.
  useEffect(
    () =>
      api.onSessionExpired(() => {
        booking.setGuestAuth(null, null);
      }),
    [api, booking],
  );

  const handleCurrencyChange = useCallback(
    (next: string) => {
      withPreferenceTransition(() => {
        if (booking.state.selectedRooms.length > 0) {
          booking.reset();
        }
        setCurrency(next);
        const search = new URLSearchParams(location.search);
        search.set('currency', next);
        void navigate(
          { pathname: location.pathname, search: search.toString() },
          { replace: true },
        );
      });
    },
    [booking, location.pathname, location.search, navigate, setCurrency, withPreferenceTransition],
  );

  const handleLanguageChange = setLanguage;

  // Every guest route is public, so signing out stays on the current page —
  // dropping a guest from checkout to home mid-booking loses their context.
  const handleSignOut = useCallback(() => {
    api.setAccessToken(null);
    booking.setGuestAuth(null, null);
    setIsMobileMenuOpen(false);
    setSignOutConfirmOpen(false);
  }, [api, booking]);

  return (
    <div className="flex min-h-screen flex-col bg-background select-none">
      {isMobileMenuOpen && (
        <Sidebar
          id="mobile-navigation"
          title={t('site.menu')}
          position="right"
          width="90%"
          onClose={closeMobileMenu}
        >
          <div className="flex h-full flex-col px-5 py-5 space-y-6">
            <div>
              <span className="mb-1.5 block text-xs font-semibold text-muted-foreground">
                {t('site.currency')}
              </span>
              <CustomSelect
                value={activeCurrency}
                onChange={handleCurrencyChange}
                options={CURRENCY_OPTIONS}
                ariaLabel={t('site.currencySelector')}
                triggerClassName="border border-gray-200 text-sm rounded-md px-4 py-3 flex items-center justify-between w-full transition-all hover:border-primary"
              />
            </div>
            <div>
              <span className="mb-1.5 block text-xs font-semibold text-muted-foreground">
                {t('site.language')}
              </span>
              <CustomSelect
                value={language}
                onChange={handleLanguageChange}
                options={LANGUAGE_OPTIONS}
                ariaLabel={t('site.languageSelector')}
                triggerClassName="border border-gray-200 text-sm rounded-md px-4 py-3 flex items-center justify-between w-full transition-all hover:border-primary"
              />
            </div>
            {isSignedIn ? (
              <button
                type="button"
                onClick={() => {
                  setSignOutConfirmOpen(true);
                }}
                className="bg-white border border-primary text-primary rounded-full px-4 py-3 text-center text-sm font-bold tracking-wide transition-all hover:bg-primary hover:text-white"
              >
                {t('site.signOut')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  closeMobileMenu();
                  setAuthDialog('sign-in');
                }}
                className="bg-white border border-primary text-primary rounded-full px-4 py-3 text-center text-sm font-bold tracking-wide transition-all hover:bg-primary hover:text-white"
              >
                {t('site.signIn')}
              </button>
            )}
          </div>
        </Sidebar>
      )}

      <header className="sticky top-0 z-40 border-b border-border bg-white">
        <div className="mx-auto flex max-w-[1800px] items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-6 2xl:gap-8">
            <Link to="/" className="shrink-0" aria-busy={!siteReady}>
              {!siteReady ? (
                <div className="flex items-center gap-4">
                  <span aria-hidden="true" className="h-8 w-8 animate-pulse rounded-md bg-muted" />
                  <span
                    aria-hidden="true"
                    className="h-5 w-24 animate-pulse rounded bg-muted sm:w-32"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-4 text-foreground divide-x-1 divide-gray-200">
                  <div className="flex items-center gap-2 pr-4">
                    <BrandMark key={site.logoUrl} name={site.name} logoUrl={site.logoUrl} />
                    {site.name !== '' && <span className="text-xl font-bold">{site.name}</span>}
                  </div>
                  <div className="hidden xl:block py-0.5">
                    {viewingProperty !== null ? (
                      <>
                        <div className="font-semibold">{viewingProperty.propertyName}</div>
                        {viewingProperty.propertyAddress !== '' && (
                          <div className="text-xs text-muted-foreground">
                            {viewingProperty.propertyAddress}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="font-semibold">{t('site.header.apartments')}</div>
                    )}
                  </div>
                </div>
              )}
            </Link>
          </div>

          <div className="hidden items-center gap-1 lg:flex">
            <CustomSelect
              value={activeCurrency}
              onChange={handleCurrencyChange}
              options={CURRENCY_OPTIONS}
              ariaLabel={t('site.currencySelector')}
              hideDropdownIcon
              triggerClassName="cursor-pointer flex items-center gap-1.5 text-xs font-semibold text-secondary-foreground transition-all hover:bg-gray-50 px-2.5 py-2 rounded-md hover:text-primary"
            />
            <CustomSelect
              value={language}
              onChange={handleLanguageChange}
              options={LANGUAGE_OPTIONS}
              ariaLabel={t('site.languageSelector')}
              hideDropdownIcon
              triggerClassName="cursor-pointer flex items-center gap-1.5 text-xs font-semibold text-secondary-foreground transition-all hover:bg-gray-50 px-2.5 py-2 rounded-md hover:text-primary"
            />
            {isSignedIn ? (
              <button
                type="button"
                onClick={() => {
                  setSignOutConfirmOpen(true);
                }}
                className="bg-white border border-primary text-primary rounded-full px-4 py-2 text-xs font-bold tracking-wide transition-all hover:bg-primary hover:text-white ml-1.5"
              >
                {t('site.signOut')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setAuthDialog('sign-in');
                }}
                className="bg-white border border-primary text-primary rounded-full px-4 py-2 text-xs font-bold tracking-wide transition-all hover:bg-primary hover:text-white ml-1.5"
              >
                {t('site.signIn')}
              </button>
            )}
          </div>

          <button
            type="button"
            aria-label={t('site.openMenu')}
            aria-controls="mobile-navigation"
            aria-expanded={isMobileMenuOpen}
            onClick={() => {
              setIsMobileMenuOpen(true);
            }}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted xl:hidden"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <LoadingScreen fullScreen visible={preferenceTransitioning} />

      <SignInDialog
        open={authDialog === 'sign-in'}
        onOpenChange={(open) => {
          setAuthDialog(open ? 'sign-in' : null);
        }}
        onJoin={() => {
          setAuthDialog('join');
        }}
      />
      <SignUpDialog
        open={authDialog === 'join'}
        onOpenChange={(open) => {
          setAuthDialog(open ? 'join' : null);
        }}
        onSignIn={() => {
          setAuthDialog('sign-in');
        }}
      />

      <Dialog open={signOutConfirmOpen} onOpenChange={setSignOutConfirmOpen}>
        <DialogContent>
          <DialogHeader className="gap-1.5 text-left">
            <DialogTitle>{t('site.signOutConfirmTitle')}</DialogTitle>
            <DialogDescription className="leading-relaxed">
              {t('site.signOutConfirmBody')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="border-t border-border bg-muted/40 -mx-6 -mb-6 px-6 py-4 sm:justify-end">
            <button
              type="button"
              className="ibe-secondary-button cursor-pointer"
              onClick={() => {
                setSignOutConfirmOpen(false);
              }}
            >
              {t('site.signOutStay')}
            </button>
            <button
              type="button"
              className="ibe-primary-button cursor-pointer"
              onClick={handleSignOut}
            >
              {t('site.signOut')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <main className="flex-1">
        <PageTitleProvider siteName={site.name}>
          <Outlet />
        </PageTitleProvider>
      </main>

      <footer className="border-t bg-white">
        <div className="mx-auto max-w-[1550px] divide-y-1 divide-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:grid-cols-4 py-8 px-3 md:px-6">
            {TRUST_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="flex items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">{t(item.title)}</h3>
                    <p className="text-sm text-muted-foreground">{t(item.description)}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="py-3 px-3 md:px-6 flex justify-center">
            <p className="text-xs text-muted-foreground">
              &copy; {new Date().getFullYear().toString()} {site.name}. {t('site.footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
